import React from 'react';
import {
  Box,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { ChevronDown } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { buildDateRangeClosedLabel } from './dateRangeFilterLabel';

export type DateRangeFilterProps = {
  from: string;
  to: string;
  onFromChange: (iso: string) => void;
  onToChange: (iso: string) => void;
  /** When true, closed label uses the "Last 90 days · …" preset (URL has no from/to override). */
  useLast90DaysPreset?: boolean;
  disabled?: boolean;
};

function isoToDateInput(iso: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function dateInputToIso(date: string, endOfDay: boolean): string {
  if (!date) return '';
  return endOfDay ? `${date}T23:59:59.999Z` : `${date}T00:00:00.000Z`;
}

/**
 * Date range dropdown: closed label shows preset + span; panel edits from/to.
 */
export function DateRangeFilter({
  from,
  to,
  onFromChange,
  onToChange,
  useLast90DaysPreset = false,
  disabled = false,
}: DateRangeFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const closedLabel = buildDateRangeClosedLabel(from, to, t, useLast90DaysPreset);

  return (
    <Box position="relative" flexShrink={0} alignSelf="flex-start">
      <Pressable
        testID="report-filter-date-range-trigger"
        disabled={disabled}
        onPress={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        flexShrink={0}
        alignSelf="flex-start"
        borderWidth={1}
        borderColor="$borderLight"
        borderRadius="$lg"
        pl="$3"
        pr={10}
        height={36}
        justifyContent="center"
        bg="$bgCard"
        opacity={disabled ? 0.5 : 1}
        sx={{ _web: { flexShrink: 0, width: 'max-content' } }}
      >
        <HStack
          testID="report-filter-date-range-row"
          space="sm"
          alignItems="center"
          flexWrap="nowrap"
          flexShrink={0}
          sx={{ _web: { flexWrap: 'nowrap', width: 'max-content' } }}
        >
          <Text
            color="$textBody"
            testID="report-filter-date-range-label"
            flexShrink={0}
            fontSize={13}
            lineHeight={18}
            fontWeight="500"
            sx={{ _web: { whiteSpace: 'nowrap' } }}
          >
            {closedLabel}
          </Text>
          <ChevronDown
            size={16}
            strokeWidth={1.75}
            color="#98A2B3"
            testID="report-filter-date-range-chevron"
          />
        </HStack>
      </Pressable>
      {!!open && (
        <Box
          testID="report-filter-date-range-panel"
          position="absolute"
          top="100%"
          mt="$1"
          zIndex={10}
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$sm"
          p="$3"
          softShadow="3"
          minWidth={280}
        >
          <VStack space="md">
            <VStack space="xs">
              <Text size="xs" color="$textSubtle">{t('admin.reports.filters.from')}</Text>
              <Input size="sm" isDisabled={disabled}>
                <InputField
                  testID="report-filter-from"
                  value={isoToDateInput(from)}
                  onChangeText={(text) => {
                    if (text.length === 10) {
                      onFromChange(dateInputToIso(text, false));
                    }
                  }}
                  placeholder={t('admin.reports.filters.datePlaceholder')}
                />
              </Input>
            </VStack>
            <VStack space="xs">
              <Text size="xs" color="$textSubtle">{t('admin.reports.filters.to')}</Text>
              <Input size="sm" isDisabled={disabled}>
                <InputField
                  testID="report-filter-to"
                  value={isoToDateInput(to)}
                  onChangeText={(text) => {
                    if (text.length === 10) {
                      onToChange(dateInputToIso(text, true));
                    }
                  }}
                  placeholder={t('admin.reports.filters.datePlaceholder')}
                />
              </Input>
            </VStack>
          </VStack>
        </Box>
      )}
    </Box>
  );
}
